import { Box, Button, Modal, TextField, Typography, useTheme, Checkbox } from "@mui/material";
import { useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import FeedbackModal from "./FeedbackModal";
import SendIcon from "../../svg/SendIcon";
import { deactivateUser } from "../../utils/api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import LinkedInIcon from "../../svg/LinkedInIcon";
import { getLinkedInAuthUrl } from "../../utils/linkedinApi";

const AccountSettings: React.FC = () => {
  const { authUser, setAuthUser } = useAuth();
  const theme = useTheme();
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const navigate = useNavigate();

  if (!authUser) return null;


  const deleteFeedbackOptions = [
    "I don't find Soopra.ai useful",
    "I found a better alternative",
    "It's too expensive / not worth the cost",
    "I only needed it for a short time",
    "I'm concerned about privacy or data",
    "It doesn't meet my needs",
    "It's too complicated / hard to use",
  ]
  const handleDeleteAccount = () => {
    setShowDeleteAccountModal(false);
    setShowFeedbackModal(true);
  };

  const handleFeedbackSubmit = async (feedback: { reason: string; otherText?: string }) => {

    // TODO: trigger actual account deletion here

    const reason = feedback.otherText ? feedback.otherText : feedback.reason;

    try {
      await deactivateUser(reason);
      toast.success("Account deleted successfully");
      setAuthUser(null);
      navigate('/creators');
    }
    catch (error) {
      toast.error("Failed to delete account.");
    }
    finally {
      setShowFeedbackModal(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ pb: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6">Account</Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: 'center',
          mb: 2,
          pt: 2,
          borderTop: `1px solid ${theme.palette.divider}`
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            width: { xs: '50%', md: "100%" }
          }}
        >
          <Typography variant="subtitle1" color="text.primary">
            <strong>Social Integrations</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Connect your social accounts to enable sharing features.
          </Typography>
        </Box>
      </Box>

      {process.env.REACT_APP_ENABLE_LINKEDIN_INTEGRATION === 'true' && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              width: { xs: '50%', md: "100%" }
            }}
          >
            <Typography variant="body1" color="text.primary">
              LinkedIn
            </Typography>
          </Box>
          <Button
            onClick={async () => {
              try {
                const response = await getLinkedInAuthUrl();
                if (response.ok) {
                  const data = await response.json();
                  if (data.url) {
                    window.location.href = data.url;
                  } else {
                    toast.error("Failed to get LinkedIn auth URL");
                  }
                } else {
                  toast.error("Failed to initiate connection");
                }
              } catch (error) {
                console.error(error);
                toast.error("An error occurred");
              }
            }}
            variant="outlined"
            startIcon={<LinkedInIcon />}
            sx={{
              minWidth: { xs: '10%', md: "10dvw" },
              maxHeight: "40px",
            }}
            style={{
              fontWeight: 700,
              color: '#0077b5',
              borderColor: '#0077b5'
            }}
          >
            Connect
          </Button>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            width: { xs: '50%', md: "100%" }
          }}
        >
          <Typography variant="subtitle1" color="text.primary">
            <strong>Delete Account</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Permanently delete your account and all associated data.
          </Typography>
        </Box>
        <Button
          onClick={() => setShowDeleteAccountModal(true)}
          variant="outlined"
          color="error"
          sx={{
            borderColor: theme.palette.error.main,
            minWidth: { xs: '10%', md: "10dvw" },
            maxHeight: "40px",
          }}
          style={{
            fontWeight: 700,
          }}
        >
          Delete Account
        </Button>
      </Box>

      {/* Confirmation modal */}
      <DeleteAccountModal
        open={showDeleteAccountModal}
        onClose={() => setShowDeleteAccountModal(false)}
        handleConfirmDelete={handleDeleteAccount}
      />

      {/* Feedback modal */}
      <FeedbackModal
        open={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        title="Help us improve"
        subtitle="Which of the following best reflects the main reason for deleting your Account?"
        options={deleteFeedbackOptions}
        otherLabel="Other"
        onSubmit={handleFeedbackSubmit}
        actions={[
          {
            label: "Delete",
            color: "error",
            disabledWhenInvalid: true,
            onClick: handleFeedbackSubmit,
          },
        ]}
      />
    </Box>
  );
};

export default AccountSettings;

interface DeleteAccountModalProps {
  open: boolean;
  onClose: () => void;
  handleConfirmDelete: () => void;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({ open, onClose, handleConfirmDelete }) => {
  const theme = useTheme();
  const [confirmText, setConfirmText] = useState("");

  const isConfirmValid = confirmText.trim().toLowerCase() === "delete";

  const modalStyle = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "calc(100% - 32px)",
    margin: "auto",
    maxWidth: "400px",
    bgcolor: theme.palette.background.paper,
    boxShadow: 24,
    borderRadius: "10px",
    p: 3,
  };

  const handleDeleteClick = () => {
    if (isConfirmValid) {
      handleConfirmDelete();
      setConfirmText("");
      onClose();
    }
  };

  const handleClose = () => {
    setConfirmText("");
    onClose();
  }
  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle} display="flex" flexDirection="column" gap={3}>
        <Box>
          <Typography variant="h6" color="error" fontWeight="bold">
            Delete Account
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to permanently delete your account and cancel any active subscriptions?
          </Typography>
        </Box>

        <TextField
          fullWidth
          label='Type "delete" to confirm'
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          size="small"
        />

        <Box display="flex" gap={1}>
          <Button onClick={handleClose} variant="outlined" sx={{ flexGrow: 1 }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteClick}
            variant="contained"
            color="error"
            disabled={!isConfirmValid}
            sx={{ flexGrow: 1 }}
          >
            Delete
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
